import { useState } from "react";
import dayjs from "dayjs";
import { getFilesFromURLs } from "../../../hooks/getFilesFromURLs";
import { countryList } from "../../../config/constants";
import { State } from "country-state-city";
import { Campaign } from "../../../types/campaign";

export type CreateCampaignViewModelType = {
    objective: number;
    setObjective: (v: number) => void;
    media: File[];
    setMedia: (v: File[]) => void;
    title: string;
    setTitle: (v: string) => void;
    link: string;
    setLink: (v: string) => void;
    headline: string;
    setHeadline: (v: string) => void;
    nickname: string;
    setNickname: (v: string) => void;
    templateId: number;
    setTemplateId: (v: number) => void;
    ageMin: string;
    setAgeMin: (v: string) => void;
    ageMax: string;
    setAgeMax: (v: string) => void;
    hittingHand: string;
    setHittingHand: (v: string) => void;
    throwingHand: string;
    setThrowingHand: (v: string) => void;
    positions: string[];
    setPositions: (v: string[]) => void;
    country: { value: string; label: string; } | null;
    setCountry: (v: { value: string; label: string; } | null) => void;
    state: { value: string; label: string; } | null;
    setState: (v: { value: string; label: string; } | null) => void;
    city: { value: string; label: string; } | null;
    setCity: (v: { value: string; label: string; } | null) => void;
    startTime: dayjs.Dayjs | null;
    setStartTime: (v: dayjs.Dayjs | null) => void;
    endTime: dayjs.Dayjs | null;
    setEndTime: (v: dayjs.Dayjs | null) => void;
    dailyBudget: string;
    setDailyBudget: (v: string) => void;
    mediaChanged: boolean;
    setMediaChanged: (v: boolean) => void;
    load: (ad: Campaign) => void;
    reset: () => void;
}

export const CreateCampaignViewModel = (): CreateCampaignViewModelType => {
    // Objective
    const [objective, setObjective] = useState(1);

     // Media Upload Fields
     const [media, setMedia] = useState<File[]>([]);
     const [title, setTitle] = useState("");
     const [link, setLink] = useState("");
     const [headline, setHeadline] = useState("");
     const [nickname, setNickname] = useState("");
     const [templateId, setTemplateId] = useState(-1);

    // Audience Targeting Fields
    const [ageMin, setAgeMin] = useState("");
    const [ageMax, setAgeMax] = useState("");
    const [hittingHand, setHittingHand] = useState("");
    const [throwingHand, setThrowingHand] = useState("");
    const [positions, setPositions] = useState<string[]>([]);
    const [country, setCountry] = useState<{ value: string; label: string; } | null>(null);
    const [state, setState] = useState<{ value: string; label: string; } | null>(null);
    const [city, setCity] = useState<{ value: string; label: string; } | null>(null);
    const [startTime, setStartTime] = useState<dayjs.Dayjs | null>(null);
    const [endTime, setEndTime] = useState<dayjs.Dayjs | null>(null);
    const [dailyBudget, setDailyBudget] = useState("10.00");

    // Tracks if new media is uploaded when updating a draft campaign
    const [mediaChanged, setMediaChanged] = useState(false);

    async function load(ad: Campaign) {
        if (ad) {
            setObjective(ad.objective ?? 1);
            getFilesFromURLs(ad.media.urls, ad.media.media).then((files) => {
                setMedia(files);
            });
            setTitle(ad.title ?? "");
            setLink(ad.link ?? "");
            setHeadline(ad.headline ?? "");
            setNickname(ad.nickname ?? "");
            setTemplateId(ad.templateId ?? -1);
            setAgeMin(ad.ageMin ?? "");
            setAgeMax(ad.ageMax ?? "");
            setHittingHand(ad.hittingHand ?? "");
            setThrowingHand(ad.throwingHand ?? "");
            setPositions(ad.positions ?? []);
            setCountry(ad.country ? countryList.find(c => c.value === ad.country) ?? null : null);
            if (ad.country) {
                const states = State.getStatesOfCountry(ad.country).map((state) => {
                    return { value: state.isoCode, label: state.name }
                });
                setState(ad.state ? states.find(s => s.value === ad.state) ?? null : null);
            }
            setCity(ad.city ? { label: ad.city, value: ad.city } : null);
            setStartTime(ad.startTime ? dayjs(ad.startTime * 1000) : null);
            setEndTime(ad.endTime ? dayjs(ad.endTime * 1000) : null);
            setDailyBudget(ad.dailyBudget ?? "10.00");
            setMediaChanged(false);
        }
    }

    // Reset all values
    function reset() {
        setObjective(1);
        setMedia([]);
        setTitle("");
        setLink("");
        setHeadline("");
        setNickname("");
        setTemplateId(-1);
        setAgeMin("");
        setAgeMax("");
        setHittingHand("");
        setThrowingHand("");
        setPositions([]);
        setCountry(null);
        setState(null);
        setCity(null);
        setStartTime(null);
        setEndTime(null);
        setDailyBudget("10.00");
        setMediaChanged(false);
    }

    return {
        objective,
        setObjective,
        media,
        setMedia,
        title,
        setTitle,
        link,
        setLink,
        headline,
        setHeadline,
        nickname,
        setNickname,
        templateId,
        setTemplateId,
        ageMin,
        setAgeMin,
        ageMax,
        setAgeMax,
        hittingHand,
        setHittingHand,
        throwingHand,
        setThrowingHand,
        positions,
        setPositions,
        country,
        setCountry,
        state,
        setState,
        city,
        setCity,
        startTime,
        setStartTime,
        endTime,
        setEndTime,
        dailyBudget,
        setDailyBudget,
        mediaChanged,
        setMediaChanged,
        load,
        reset
    }
}
